import React from 'react';
import { Dropdown, Typography, Tooltip, theme as antdTheme } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';

import type { ThemeConfig } from '../../types';

interface ThemePreviewCardProps {
    name: string;
    theme: ThemeConfig;
    isDefault?: boolean;
    isPublic?: boolean;
    onEdit?: () => void;
    onDelete?: () => void;
    onUpdatePublic?: () => void;
    onApply?: () => void;
    applyLabel?: string;
}

const ThemePreviewCard = ({
    name,
    theme,
    isDefault,
    isPublic,
    onEdit,
    onDelete,
    onUpdatePublic,
    onApply,
    applyLabel = 'Применить',
}: ThemePreviewCardProps) => {
    // Current app theme (settings modal) — not the previewed theme
    const { token } = antdTheme.useToken();

    const {
        colorPrimary,
        borderRadius,
        colorTextBase,
        colorBgBase,
        isCompact,
        isDark,
        fontFamily,
        backgroundImage,
        cardTransparency,
    } = theme;

    const items = [{
        key: 'apply',
        label: applyLabel,
        onClick: onApply,
    }];

    if (onEdit) {
        items.push({
            key: 'edit',
            label: 'Редактировать',
            onClick: onEdit,
        });
    }

    if (!isDefault && onUpdatePublic) {
        items.push({
            key: 'public',
            label: isPublic ? 'Сделать приватной' : 'Сделать публичной',
            onClick: onUpdatePublic,
        });
    }

    if (!isDefault && onDelete) {
        items.push({
            key: 'delete',
            label: 'Удалить',
            onClick: onDelete,
        });
    }

    const panel = isDark ? '#2b2b2b' : '#ffffff';
    const layoutBg = isDark ? '#1a1a1a' : (colorBgBase || '#f5f5f5');

    const previewStyle = {
        ['--preview-bg' as string]: layoutBg,
        ['--preview-panel' as string]: panel,
        ['--preview-primary' as string]: colorPrimary,
        ['--preview-text' as string]: isDark ? '#e5e5e5' : (colorTextBase || '#000'),
        ['--preview-image' as string]: backgroundImage ? `url(${backgroundImage})` : 'none',
        ['--preview-blur' as string]: cardTransparency ? 'blur(6px)' : 'none',
        borderRadius,
        fontFamily: fontFamily || 'inherit',
        border: `1px solid ${token.colorBorder}`,
        background: token.colorBgElevated,
        overflow: 'hidden',
    } as React.CSSProperties;

    return (
        <Dropdown menu={{ items }} trigger={['contextMenu']}>
            <div className="theme-preview" style={previewStyle} onClick={onApply}>
                <div className="theme-preview__shell">
                    <div className="theme-preview__bar" />
                    <div className="theme-preview__cols">
                        <div className="theme-preview__col">
                            <div
                                style={{
                                    height: 6,
                                    width: '60%',
                                    borderRadius: 3,
                                    background: colorPrimary,
                                    opacity: 0.7,
                                }}
                            />
                            <div className="theme-preview__card" />
                            <div className="theme-preview__card" style={{ height: 18, opacity: 0.7 }} />
                        </div>
                        <div className="theme-preview__col">
                            <div
                                style={{
                                    height: 6,
                                    width: '45%',
                                    borderRadius: 3,
                                    background: colorPrimary,
                                    opacity: 0.45,
                                }}
                            />
                            <div className="theme-preview__card" style={{ height: 34 }} />
                        </div>
                    </div>
                </div>
                <div
                    className="theme-preview__meta"
                    style={{
                        background: token.colorBgElevated,
                        borderTop: `1px solid ${token.colorBorderSecondary}`,
                        color: token.colorText,
                    }}
                >
                    <p className="theme-preview__name" style={{ color: token.colorText }}>
                        {name}
                        {isDefault &&
                            <Tooltip title="Официальная тема">
                                <CheckCircleOutlined style={{ marginLeft: 8, color: token.colorPrimary }} />
                            </Tooltip>
                        }
                    </p>
                    <Typography.Text style={{ fontSize: 12, color: token.colorTextSecondary }}>
                        {isDark ? 'Тёмная' : 'Светлая'}
                        {isCompact ? ' · Компакт' : ''}
                        {cardTransparency ? ' · Стекло' : ''}
                        {isPublic ? ' · Публичная' : ''}
                    </Typography.Text>
                </div>
            </div>
        </Dropdown>
    );
};

export default ThemePreviewCard;
